<script setup>
/* global axios */
import { onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useRouter } from 'vue-router';
import TdfAPI from 'dashboard/api/tdfConfig';

const { t } = useI18n();
const { accountScopedRoute } = useAccount();
const router = useRouter();

const stats = ref(null);
const isLoading = ref(false);

const modules = [
  {
    key: 'reception',
    icon: 'i-lucide-bell-ring',
    routeName: 'tdf_reception',
    items: [
      'TDF.RECEPTION.MAP_OF_DAY',
      'TDF.RECEPTION.PARKING',
      'TDF.RECEPTION.HOUSEKEEPING',
      'TDF.RECEPTION.HOUSEKEEPING_LISTS',
      'TDF.RECEPTION.HOUR_MAP',
    ],
  },
  {
    key: 'reservations',
    icon: 'i-lucide-calendar-days',
    routeName: 'tdf_reservations',
    items: [
      'TDF.RESERVATIONS.MAP_7_DAYS',
      'TDF.RESERVATIONS.MAP_14_DAYS',
      'TDF.RESERVATIONS.MAP_21_DAYS',
      'TDF.RESERVATIONS.MAP_31_DAYS',
      'TDF.RESERVATIONS.AVAILABILITY_MAP',
      'TDF.RESERVATIONS.QUOTATION',
      'TDF.RESERVATIONS.TARIFF',
    ],
  },
  {
    key: 'direct-sales',
    icon: 'i-lucide-shopping-cart',
    routeName: 'tdf_direct_sales',
    items: [
      'TDF.DIRECT_SALES.POS',
      'TDF.DIRECT_SALES.PRODUCTS',
      'TDF.DIRECT_SALES.MOBILE_ORDERS',
      'TDF.DIRECT_SALES.ORDERS_REPORT',
    ],
  },
  {
    key: 'clients',
    icon: 'i-lucide-users',
    routeName: 'tdf_clients',
    items: [
      'TDF.CLIENTS.PORTFOLIO',
      'TDF.CLIENTS.SATISFACTION_SURVEY',
      'TDF.CLIENTS.DIRECT_MAIL_CRM',
      'TDF.CLIENTS.LOYALTY',
    ],
  },
  {
    key: 'admin',
    icon: 'i-lucide-chart-bar',
    routeName: 'tdf_admin',
    items: [
      'TDF.ADMIN.FINANCIAL',
      'TDF.ADMIN.INVENTORY',
      'TDF.ADMIN.SUPPLIER_CONTACTS',
      'TDF.ADMIN.GUEST_REGISTRY',
    ],
  },
  {
    key: 'other-routines',
    icon: 'i-lucide-settings',
    routeName: 'tdf_other_routines',
    items: [
      'TDF.OTHER_ROUTINES.PHONE_TARIFF',
      'TDF.OTHER_ROUTINES.HOSTING_REPORT',
      'TDF.OTHER_ROUTINES.SETTINGS',
    ],
  },
];

const navigateToModule = routeName => {
  router.push(accountScopedRoute(routeName));
};

const fetchStats = async () => {
  isLoading.value = true;
  try {
    const { data } = await TdfAPI.dashboardStats();
    stats.value = data;
  } catch {
    stats.value = null;
  } finally {
    isLoading.value = false;
  }
};

const formatCurrency = value => {
  return Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

onBeforeMount(() => {
  fetchStats();
});
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Stats Cards -->
    <div v-if="stats" class="grid grid-cols-2 gap-4 md:grid-cols-4">
      <div
        class="flex flex-col gap-1 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-600 dark:bg-slate-800"
      >
        <span
          class="text-xs font-medium uppercase text-slate-500 dark:text-slate-400"
        >
          {{ t('TDF.DASHBOARD.TODAY_CHECK_INS') }}
        </span>
        <span class="text-2xl font-bold text-blue-600">{{
          stats.today_check_ins
        }}</span>
      </div>
      <div
        class="flex flex-col gap-1 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-600 dark:bg-slate-800"
      >
        <span
          class="text-xs font-medium uppercase text-slate-500 dark:text-slate-400"
        >
          {{ t('TDF.DASHBOARD.TODAY_CHECK_OUTS') }}
        </span>
        <span class="text-2xl font-bold text-orange-600">{{
          stats.today_check_outs
        }}</span>
      </div>
      <div
        class="flex flex-col gap-1 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-600 dark:bg-slate-800"
      >
        <span
          class="text-xs font-medium uppercase text-slate-500 dark:text-slate-400"
        >
          {{ t('TDF.DASHBOARD.OCCUPANCY') }}
        </span>
        <span class="text-2xl font-bold text-green-600">{{ stats.occupancy_today }}%</span>
      </div>
      <div
        class="flex flex-col gap-1 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-600 dark:bg-slate-800"
      >
        <span
          class="text-xs font-medium uppercase text-slate-500 dark:text-slate-400"
        >
          {{ t('TDF.DASHBOARD.REVENUE_MONTH') }}
        </span>
        <span class="text-2xl font-bold text-emerald-600">{{
          formatCurrency(stats.revenue_this_month)
        }}</span>
      </div>
    </div>

    <!-- Module Cards Grid -->
    <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
      <div
        v-for="mod in modules"
        :key="mod.key"
        class="flex flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-md dark:border-slate-600 dark:bg-slate-800"
      >
        <!-- Card Header -->
        <div
          class="flex cursor-pointer items-center justify-between bg-gradient-to-r from-blue-500 to-blue-600 px-4 py-3"
          @click="navigateToModule(mod.routeName)"
        >
          <span class="text-sm font-bold uppercase tracking-wide text-white">
            {{ t(`TDF.MODULES.${mod.key.toUpperCase().replace('-', '_')}`) }}
          </span>
          <span class="size-6 text-white opacity-80" :class="mod.icon" />
        </div>
        <!-- Card Body -->
        <div
          class="flex flex-col divide-y divide-slate-100 dark:divide-slate-700"
        >
          <a
            v-for="(item, idx) in mod.items"
            :key="idx"
            class="flex items-center px-4 py-2.5 text-sm text-blue-600 transition-colors hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-slate-750 cursor-pointer"
            @click="navigateToModule(mod.routeName)"
          >
            {{ t(item) }}
          </a>
        </div>
      </div>
    </div>

    <!-- Recent Bookings from Motor de Reserva -->
    <div
      v-if="stats && stats.recent_bookings && stats.recent_bookings.length > 0"
      class="flex flex-col gap-3"
    >
      <h3 class="text-base font-medium text-slate-800 dark:text-slate-100">
        {{ t('TDF.DASHBOARD.RECENT_BOOKINGS') }}
      </h3>
      <div
        class="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-600"
      >
        <table class="w-full text-sm">
          <thead>
            <tr class="bg-slate-50 dark:bg-slate-700">
              <th
                class="px-3 py-2 text-left text-xs font-medium uppercase text-slate-500 dark:text-slate-300"
              >
                ID
              </th>
              <th
                class="px-3 py-2 text-left text-xs font-medium uppercase text-slate-500 dark:text-slate-300"
              >
                {{ t('TDF.DASHBOARD.GUEST') }}
              </th>
              <th
                class="px-3 py-2 text-left text-xs font-medium uppercase text-slate-500 dark:text-slate-300"
              >
                Check-in
              </th>
              <th
                class="px-3 py-2 text-left text-xs font-medium uppercase text-slate-500 dark:text-slate-300"
              >
                Check-out
              </th>
              <th
                class="px-3 py-2 text-center text-xs font-medium uppercase text-slate-500 dark:text-slate-300"
              >
                Status
              </th>
              <th
                class="px-3 py-2 text-right text-xs font-medium uppercase text-slate-500 dark:text-slate-300"
              >
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="booking in stats.recent_bookings"
              :key="booking.id"
              class="border-t border-slate-100 hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-slate-750"
            >
              <td class="px-3 py-2 text-slate-700 dark:text-slate-200">
                #{{ booking.id }}
              </td>
              <td class="px-3 py-2 text-slate-700 dark:text-slate-200">
                {{ booking.contact?.name || booking.guest_name || '-' }}
              </td>
              <td class="px-3 py-2 text-slate-700 dark:text-slate-200">
                {{ new Date(booking.check_in).toLocaleDateString('pt-BR') }}
              </td>
              <td class="px-3 py-2 text-slate-700 dark:text-slate-200">
                {{ new Date(booking.check_out).toLocaleDateString('pt-BR') }}
              </td>
              <td class="px-3 py-2 text-center">
                <span
                  class="rounded-full px-2 py-0.5 text-xs"
                  :class="{
                    'bg-yellow-100 text-yellow-700':
                      booking.status === 'budget',
                    'bg-green-100 text-green-700':
                      booking.status === 'confirmed',
                    'bg-blue-100 text-blue-700':
                      booking.status === 'checked_in',
                    'bg-slate-100 text-slate-600':
                      booking.status === 'checked_out',
                    'bg-red-100 text-red-700': booking.status === 'cancelled',
                  }"
                >
                  {{ booking.status }}
                </span>
              </td>
              <td
                class="px-3 py-2 text-right font-medium text-slate-800 dark:text-slate-100"
              >
                {{ formatCurrency(booking.total_price) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
